import { warn } from "vue";
import type { Confirmer, ConfirmExternal, ConfirmNoCallback, CustomConfirmExternal } from "./types";

class Observer {
	confirmer: Confirmer | null;

	constructor() {
		this.confirmer = null;
	}

	register = (confirmer: Confirmer) => {
		if (this.confirmer != null) {
			warn("[UI] A ConfirmProvider was already registered. It will be overwritten.");
		}
		this.confirmer = confirmer;

		return () => {
			this.confirmer = null;
		};
	};

	publish = (confirm: ConfirmNoCallback) => {
		if (this.confirmer == null) {
			warn(
				"[UI] Using confirm without a ConfirmProvider registered. Please add a ConfirmProvider in the root of the app"
			);
			return new Promise<boolean>((resolve) => resolve(false));
		}

		return new Promise<boolean>((resolve) => {
			this.confirmer?.({
				...confirm,
				onConfirm: () => resolve(true),
				onCancel: () => resolve(false),
			});
		});
	};

	confirm = (message: string, data: ConfirmExternal = {}) => {
		return this.publish({
			type: "confirm",
			title: message,
			...data,
		});
	};

	yesNo = (message: string, data: ConfirmExternal = {}) => {
		return this.publish({
			type: "yesNo",
			title: message,
			...data,
		});
	};

	delete = (message: string, data: ConfirmExternal = {}) => {
		return this.publish({
			type: "delete",
			title: message,
			...data,
		});
	};

	custom = (message: string, data: CustomConfirmExternal) => {
		return this.publish({
			type: "custom",
			title: message,
			...data,
		});
	};
}

export const ConfirmState = new Observer();

function confirmFn(message: string, data?: ConfirmExternal) {
	return ConfirmState.confirm(message, data || {});
}

export const confirm = Object.assign(confirmFn, {
	confirm: ConfirmState.confirm,
	yesNo: ConfirmState.yesNo,
	delete: ConfirmState.delete,
	custom: ConfirmState.custom,
});
